import React from "react";

export type ChipColor =
  | "default"
  | "fuchsia"
  | "blue"
  | "red"
  | "green"
  | "amber";

export interface IChipOptionsConfig {
  color: ChipColor;
  logo: React.ReactNode;
  label: string;
  prefix?: string;
}

export interface ChipSourceMeta {
  sourceImage?: string;
  sourceIconName?: string;
  sourceColor?: string;
  sourceName?: string;
  sourceKind?: "node" | "secret";
  dataType?: string;
  isList?: boolean;
}

export type ChipOptionValue =
  | string
  | ({ value: string; label: string } & ChipSourceMeta);

export interface AdvancedChipOption {
  label: string;
  icon?: React.ReactNode;
  color?: ChipColor;
  prefix?: string;
  values: ChipOptionValue[];
}

export interface MentionSuggestionItem extends ChipSourceMeta {
  /** Unique chip identifier — stored on the mention node's `attrs.id` and used
   *  as the runtime placeholder (`((id))`). For object-form chip values this is
   *  `value.value`; for legacy string values it's the string itself. */
  id: string;
  /** Display name. Equal to `id` for legacy string values; for object values
   *  it's `value.label` (which may collide with another chip's label). */
  label: string;
  group: string;
  prefix?: string;
  color?: ChipColor;
  icon?: React.ReactNode;
}

export function resolveChipValueId(value: ChipOptionValue): string {
  return typeof value === "string" ? value : value.value;
}

export function resolveChipValueLabel(value: ChipOptionValue): string {
  return typeof value === "string" ? value : value.label;
}

function resolveChipValueMeta(value: ChipOptionValue): ChipSourceMeta {
  if (typeof value === "string") return {};
  return {
    sourceImage: value.sourceImage,
    sourceColor: value.sourceColor,
    sourceName: value.sourceName,
    sourceKind: value.sourceKind,
    dataType: value.dataType,
    isList: value.isList,
  };
}

export function chipOptionsToSuggestionItems(
  groups: AdvancedChipOption[]
): MentionSuggestionItem[] {
  return groups.flatMap((group) =>
    group.values.map((value) => ({
      id: resolveChipValueId(value),
      label: resolveChipValueLabel(value),
      group: group.label,
      prefix: group.prefix,
      color: group.color,
      icon: group.icon,
      ...resolveChipValueMeta(value),
    }))
  );
}

export function allChipValues(groups: AdvancedChipOption[]): string[] {
  return groups.flatMap((g) =>
    g.values.map((v) => `${g?.prefix || ""}${resolveChipValueId(v)}`)
  );
}

export function hasChipValues(
  groups: AdvancedChipOption[] | undefined
): boolean {
  return !!groups?.some((g) => g.values.length > 0);
}

export interface ChipInputSpec {
  name: string;
  kind: "value" | "reference";
}

/** Build typed specs for backend consumers. Reference group prefix "@" → kind:"reference".
 *  All other prefixes (including none) collapse to kind:"value" — the refiner treats them
 *  as value placeholders at runtime. */
export function allChipSpecs(groups: AdvancedChipOption[]): ChipInputSpec[] {
  return groups.flatMap((g) =>
    g.values.map((v) => ({
      name: resolveChipValueId(v),
      kind:
        g.prefix === REFERENCE_MENTION_CONFIG.prefix
          ? ("reference" as const)
          : ("value" as const),
    }))
  );
}

export const VALUE_MENTION_CONFIG: IChipOptionsConfig = {
  color: "default",
  logo: null,
  label: "Value",
};

export const REFERENCE_MENTION_CONFIG: IChipOptionsConfig = {
  color: "fuchsia",
  logo: <span className="font-semibold opacity-70">@</span>,
  label: "Reference",
  prefix: "@",
};

export const CHIP_BADGE_CLASSES: Record<ChipColor, string> = {
  default: "",
  fuchsia:
    "bg-[#fae8ff] border-[#f0abfc] text-foreground hover:bg-[#f0abfc] dark:bg-[#2e1336] dark:border-[#4a1d5e] dark:hover:bg-[#4a1d5e]",
  blue: "bg-blue-50 border-blue-200 text-foreground hover:bg-blue-200 dark:bg-blue-950 dark:border-blue-800 dark:hover:bg-blue-800",
  red: "bg-red-50 border-red-200 text-foreground hover:bg-red-200 dark:bg-red-950 dark:border-red-800 dark:hover:bg-red-800",
  green:
    "bg-green-50 border-green-200 text-foreground hover:bg-green-200 dark:bg-green-950 dark:border-green-800 dark:hover:bg-green-800",
  amber:
    "bg-amber-50 border-amber-200 text-foreground hover:bg-amber-200 dark:bg-amber-950 dark:border-amber-800 dark:hover:bg-amber-800",
};

export const CHIP_EDITOR_BASE = [
  "mention-styled border rounded text-foreground",
  "px-2 py-[0.1rem] text-xs font-medium",
  "!whitespace-nowrap cursor-move select-none",
  "before:content-[attr(data-icon)] before:mr-[0.15rem] before:font-semibold before:opacity-70",
  "[&.dragging]:opacity-40 active:opacity-40 selection:bg-transparent",
].join(" ");

export const CHIP_EDITOR_COLORS: Record<ChipColor, string> = {
  default: [
    "bg-secondary border-border hover:bg-accent",
    "[&.selected]:bg-[#d8e9f7] [&.selected]:border-[#689dc6]",
    "dark:bg-accent dark:[&.selected]:bg-accent dark:[&.selected]:border-ring",
  ].join(" "),
  fuchsia: [
    "bg-[#fae8ff] border-[#f0abfc] hover:bg-[#f0abfc]",
    "[&.selected]:bg-[#f0abfc] [&.selected]:border-[#d946ef]",
    "dark:bg-[#2e1336] dark:border-[#4a1d5e] dark:hover:bg-[#4a1d5e]",
    "dark:[&.selected]:bg-[#4a1d5e] dark:[&.selected]:border-[#d946ef]",
  ].join(" "),
  blue: [
    "bg-blue-50 border-blue-200 hover:bg-blue-200",
    "[&.selected]:bg-blue-200 [&.selected]:border-blue-400",
    "dark:bg-blue-950 dark:border-blue-800 dark:hover:bg-blue-800",
    "dark:[&.selected]:bg-blue-800 dark:[&.selected]:border-blue-400",
  ].join(" "),
  red: [
    "bg-red-50 border-red-200 hover:bg-red-200",
    "[&.selected]:bg-red-200 [&.selected]:border-red-400",
    "dark:bg-red-950 dark:border-red-800 dark:hover:bg-red-800",
    "dark:[&.selected]:bg-red-800 dark:[&.selected]:border-red-400",
  ].join(" "),
  green: [
    "bg-green-50 border-green-200 hover:bg-green-200",
    "[&.selected]:bg-green-200 [&.selected]:border-green-400",
    "dark:bg-green-950 dark:border-green-800 dark:hover:bg-green-800",
    "dark:[&.selected]:bg-green-800 dark:[&.selected]:border-green-400",
  ].join(" "),
  amber: [
    "bg-amber-50 border-amber-200 hover:bg-amber-200",
    "[&.selected]:bg-amber-200 [&.selected]:border-amber-400",
    "dark:bg-amber-950 dark:border-amber-800 dark:hover:bg-amber-800",
    "dark:[&.selected]:bg-amber-800 dark:[&.selected]:border-amber-400",
  ].join(" "),
};

export function getEditorMentionClasses(
  color: string | undefined,
  nodeClass: string | null
): string {
  const chipColor = (color as ChipColor) || "default";
  const colorClasses =
    CHIP_EDITOR_COLORS[chipColor] || CHIP_EDITOR_COLORS["default"];
  return `${CHIP_EDITOR_BASE} ${colorClasses} ${nodeClass || ""}`.trim();
}

export function buildMentionToken(
  id: string,
  config: IChipOptionsConfig | { prefix?: string }
): string {
  return config.prefix ? `((${config.prefix}${id}))` : `((${id}))`;
}

export function parseMentionToken(token: string): {
  id: string;
  config: IChipOptionsConfig;
} {
  const match = token.match(/^\(\((@?)([^)]+)\)\)$/);
  if (!match) {
    return { id: token, config: VALUE_MENTION_CONFIG };
  }
  const prefix = match[1];
  const id = match[2];
  if (prefix === REFERENCE_MENTION_CONFIG.prefix) {
    return { id, config: REFERENCE_MENTION_CONFIG };
  }
  return { id, config: VALUE_MENTION_CONFIG };
}

/** Extract all mention IDs from a Tiptap JSON document structure.
 *  Unlike regex on getText(), this is unambiguous for IDs containing parentheses. */
export function getEditorMentionIds(doc: Record<string, unknown>): string[] {
  const ids = new Set<string>();
  const walk = (node: Record<string, unknown>) => {
    if (node.type === "mention" || node.type === "referenceMention") {
      const attrs = node.attrs as Record<string, unknown> | undefined;
      if (attrs?.id && typeof attrs.id === "string") {
        ids.add(attrs.id);
      }
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child as Record<string, unknown>);
      }
    }
  };
  walk(doc);
  return Array.from(ids);
}
